import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { householdAPI } from '../services/api';
import CreateHouseholdModal from './CreateHouseholdModal';
import JoinHouseholdModal from './JoinHouseholdModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LeaveHouseholdModal from './LeaveHouseholdModal';
import './Households.css';

function Households() {
  const navigate = useNavigate();
  const [households, setHouseholds] = useState([]);
  const [deletedHouseholds, setDeletedHouseholds] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [rejectedRequests, setRejectedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [householdToDelete, setHouseholdToDelete] = useState(null);
  const [householdToLeave, setHouseholdToLeave] = useState(null);
  const [expenseCount, setExpenseCount] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadHouseholds();
    loadDeletedHouseholds();
    loadPendingRequests();
    loadRejectedRequests();
  }, []);

  const loadHouseholds = async () => {
    try {
      const response = await api.get('/household');
      setHouseholds(response.data);
    } catch (error) {
      console.error('Error loading households:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeletedHouseholds = async () => {
    try {
      const response = await householdAPI.listDeleted();
      setDeletedHouseholds(response.data);
    } catch (error) {
      console.error('Error loading deleted households:', error);
    }
  };

  const loadPendingRequests = async () => {
    try {
      const response = await api.get('/household/my-pending-requests');
      setPendingRequests(response.data.pending_requests || []);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadRejectedRequests = async () => {
    try {
      const response = await api.get('/household/my-rejected-requests');
      setRejectedRequests(response.data.rejected_requests || []);
    } catch (error) {
      console.error('Error loading rejected requests:', error);
    }
  };

  const handleCancelRequest = async (householdId) => {
    if (!window.confirm('Are you sure you want to cancel this join request?')) return;
    
    try {
      await api.post(`/household/${householdId}/leave?action=leave`);
      loadPendingRequests();
    } catch (error) {
      console.error('Error canceling request:', error);
      alert('Failed to cancel request: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDismissRejected = async (householdId) => {
    try {
      await api.delete(`/household/my-rejected-requests/${householdId}`);
      loadRejectedRequests();
    } catch (error) {
      console.error('Error dismissing rejected request:', error);
      alert('Failed to dismiss: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    loadHouseholds();
  };

  const handleJoinSuccess = () => {
    setShowJoinModal(false);
    loadHouseholds();
    loadPendingRequests();
  };

  const handleDelete = async (household) => {
    // Get expense count
    const expenses = await householdAPI.getExpenseCount(household.id);
    setExpenseCount(expenses);
    setMemberCount(household.member_count);
    setHouseholdToDelete(household);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (deleteType) => {
    if (!householdToDelete) return;

    try {
      const permanent = deleteType === 'permanent';
      await householdAPI.delete(householdToDelete.id, permanent);
      
      loadHouseholds();
      loadDeletedHouseholds();
      
      setShowDeleteModal(false);
      setHouseholdToDelete(null);
    } catch (error) {
      console.error('Error deleting household:', error);
      alert('Failed to delete household: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRestore = async (householdId) => {
    try {
      await householdAPI.restore(householdId);
      loadHouseholds();
      loadDeletedHouseholds();
    } catch (error) {
      console.error('Error restoring household:', error);
      alert('Failed to restore household: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleLeave = (household) => {
    setHouseholdToLeave(household);
    setShowLeaveModal(true);
  };

  const handleLeaveConfirm = async (action) => {
    if (!householdToLeave) return;

    try {
      await api.post(`/household/${householdToLeave.id}/leave?action=${action}`);
      
      loadHouseholds();
      loadDeletedHouseholds();
      
      setShowLeaveModal(false);
      setHouseholdToLeave(null);
    } catch (error) {
      console.error('Error leaving household:', error);
      alert('Failed to leave household: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRejoin = async (householdId) => {
    try {
      await api.post(`/household/${householdId}/rejoin`);
      loadHouseholds();
      loadDeletedHouseholds();
    } catch (error) {
      console.error('Error rejoining household:', error);
      alert('Failed to rejoin household: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="households-page">
      <div className="households-header">
        <h1>🏠 My Households</h1>
        <div className="households-actions">
          <button onClick={() => setShowCreateModal(true)} className="create-btn">
            + Create Household
          </button>
          <button onClick={() => setShowJoinModal(true)} className="join-btn">
            🔗 Join Household
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading households...</div>
      ) : (
        <>
          {/* Tabs */}
          <div className="household-tabs">
            <button 
              className={`household-tab ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              <span className="tab-icon">🏠</span>
              <span className="tab-text">Active</span>
              <span className="tab-count">{households.length}</span>
            </button>
            <button 
              className={`household-tab ${activeTab === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending')}
            >
              <span className="tab-icon">⏳</span>
              <span className="tab-text">Pending</span>
              <span className="tab-count">{pendingRequests.length}</span>
            </button>
            <button 
              className={`household-tab rejected ${activeTab === 'rejected' ? 'active' : ''}`}
              onClick={() => setActiveTab('rejected')}
            >
              <span className="tab-icon">❌</span>
              <span className="tab-text">Rejected</span>
              <span className="tab-count">{rejectedRequests.length}</span>
            </button>
            <button 
              className={`household-tab ${activeTab === 'deleted' ? 'active' : ''}`}
              onClick={() => setActiveTab('deleted')}
            >
              <span className="tab-icon">🗄️</span>
              <span className="tab-text">Archived</span>
              <span className="tab-count">{deletedHouseholds.length}</span>
            </button>
          </div>

          {/* Active Households Tab */}
          {activeTab === 'active' && (
            households.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🏠</div>
                <h2>No Households Yet</h2>
                <p>Create a household to track shared expenses with family or roommates,</p>
                <p>or join an existing household using an invite code.</p>
                <div className="empty-actions">
                  <button onClick={() => setShowCreateModal(true)} className="create-btn-large">
                    + Create Your First Household
                  </button>
                  <button onClick={() => setShowJoinModal(true)} className="join-btn-large">
                    🔗 Join with Invite Code
                  </button>
                </div>
              </div>
            ) : (
              <div className="households-grid">
                {households.map((household) => (
                  <div 
                    key={household.id} 
                    className="household-card"
                    onClick={() => navigate(`/household/${household.id}`)}
                  >
                    <div className="household-icon">🏠</div>
                    <h2>{household.name}</h2>
                    <div className="household-info">
                      <div className="info-item">
                        <span className="label">Role:</span>
                        <span className={`value role-${household.role}`}>{household.role}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Members:</span>
                        <span className="value">{household.member_count}</span>
                      </div>
                    </div>
                    <div className="invite-code-section">
                      <span className="invite-label">Invite Code:</span>
                      <code className="invite-code">{household.invite_code}</code>
                    </div>
                    <div className="household-card-actions">
                      <button className="view-btn">View Details →</button>
                      {household.role === 'owner' ? (
                        <button 
                          className="delete-btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(household);
                          }}
                        >
                          🗑️ Delete
                        </button>
                      ) : (
                        <button 
                          className="leave-btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLeave(household);
                          }}
                        >
                          🚪 Leave
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Pending Requests Tab */}
          {activeTab === 'pending' && (
            pendingRequests.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">⏳</div>
                <h3>No Pending Requests</h3>
                <p>When you request to join a household, it will appear here until the owner approves.</p>
                <button onClick={() => setShowJoinModal(true)} className="join-btn-large">
                  🔗 Join a Household
                </button>
              </div>
            ) : (
              <div className="households-grid">
                {pendingRequests.map((request) => (
                  <div key={request.household_id} className="household-card pending">
                    <div className="pending-banner">⏳ Awaiting Approval</div>
                    <div className="household-icon">🏠</div>
                    <h2>{request.household_name}</h2>
                    <div className="household-info">
                      <div className="info-item">
                        <span className="label">Requested:</span>
                        <span className="value">
                          {new Date(request.requested_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    <p className="pending-description">
                      Your request is waiting for the household owner to approve.
                    </p>
                    <div className="household-card-actions">
                      <button 
                        className="cancel-request-btn"
                        onClick={() => handleCancelRequest(request.household_id)}
                      >
                        ✗ Cancel Request
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Rejected Requests Tab */}
          {activeTab === 'rejected' && (
            rejectedRequests.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">✅</div>
                <h3>No Rejected Requests</h3>
                <p>Great! You don't have any rejected join requests.</p>
              </div>
            ) : (
              <div className="households-grid">
                {rejectedRequests.map((request) => (
                  <div key={request.household_id} className="household-card rejected">
                    <div className="rejected-banner">❌ Not Approved</div>
                    <div className="household-icon">🏠</div>
                    <h2>{request.household_name}</h2>
                    <div className="household-info">
                      <div className="info-item">
                        <span className="label">Requested:</span>
                        <span className="value">
                          {new Date(request.requested_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>
                    <p className="rejected-description">
                      The household owner did not approve your request to join.
                    </p>
                    <div className="household-card-actions">
                      <button 
                        className="dismiss-btn"
                        onClick={() => handleDismissRejected(request.household_id)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {/* Deleted Households Tab */}
          {activeTab === 'deleted' && (
            deletedHouseholds.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📦</div>
                <h3>No Deleted Households</h3>
                <p>Households you archive will appear here and can be restored.</p>
              </div>
            ) : (
              <div className="households-grid">
                {deletedHouseholds.map((household) => (
                  <div key={household.id} className="household-card deleted">
                    <div className="deleted-banner">🗄️ Archived</div>
                    <div className="household-icon">🏠</div>
                    <h2>{household.name}</h2>
                    <div className="household-info">
                      <div className="info-item">
                        <span className="label">Role:</span>
                        <span className={`value role-${household.role}`}>{household.role}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Members:</span>
                        <span className="value">{household.member_count}</span>
                      </div>
                    </div>
                    <div className="invite-code-section">
                      <span className="invite-label">Invite Code:</span>
                      <code className="invite-code">{household.invite_code}</code>
                    </div>
                    <div className="household-actions-deleted">
                      {household.role === 'owner' ? (
                        <>
                          <button 
                            className="restore-btn"
                            onClick={() => handleRestore(household.id)}
                          >
                            ↩️ Restore
                          </button>
                          <button 
                            className="delete-permanently-btn"
                            onClick={() => handleDelete(household)}
                          >
                            Delete Permanently
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="restore-btn"
                            onClick={() => handleRejoin(household.id)}
                          >
                            ↩️ Rejoin
                          </button>
                          <button 
                            className="delete-permanently-btn"
                            onClick={() => handleLeaveConfirm('leave')}
                          >
                            Remove Permanently
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </>
      )}

      {showCreateModal && (
        <CreateHouseholdModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {showJoinModal && (
        <JoinHouseholdModal
          onClose={() => setShowJoinModal(false)}
          onSuccess={handleJoinSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && householdToDelete && (
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setHouseholdToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          itemType="Household"
          itemName={householdToDelete.name}
          showArchiveOption={true}
          relatedItemsCount={expenseCount + memberCount}
        />
      )}

      {/* Leave Household Modal */}
      {showLeaveModal && householdToLeave && (
        <LeaveHouseholdModal
          isOpen={showLeaveModal}
          onClose={() => {
            setShowLeaveModal(false);
            setHouseholdToLeave(null);
          }}
          onConfirm={handleLeaveConfirm}
          householdName={householdToLeave.name}
        />
      )}
    </div>
  );
}

export default Households;
